/**
 * Client-side XP & Level Calculation Utilities
 * For frontend display and calculations
 */

/**
 * Calculate level from total XP
 * Formula: level = floor(sqrt(totalXP / 100))
 * @param {Number} totalXP
 * @returns {Number} Level
 */
export function calculateLevel(totalXP) {
  return Math.max(1, Math.floor(Math.sqrt(totalXP / 100)));
}

/**
 * Calculate XP needed for next level
 * @param {Number} currentLevel
 * @returns {Number} XP needed to reach next level
 */
export function getXPForLevel(currentLevel) {
  return (currentLevel + 1) ** 2 * 100;
}

/**
 * Calculate XP progress towards next level
 * @param {Number} totalXP
 * @returns {Object} { currentLevelXP, nextLevelXP, progress (0-100) }
 */
export function getLevelProgress(totalXP) {
  const level = calculateLevel(totalXP);
  const currentLevelXP = level ** 2 * 100;
  const nextLevelXP = (level + 1) ** 2 * 100;
  const progress = Math.round(((totalXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100);

  return {
    currentLevelXP,
    nextLevelXP,
    progress: Math.min(100, Math.max(0, progress)),
    xpToNext: nextLevelXP - totalXP
  };
}

/**
 * Format XP for display
 * @param {Number} xp
 * @returns {String} Formatted XP (e.g., "1.2K", "50.5M")
 */
export function formatXP(xp) {
  if (xp >= 1000000) {
    return (xp / 1000000).toFixed(1) + 'M';
  } else if (xp >= 1000) {
    return (xp / 1000).toFixed(1) + 'K';
  }
  return xp.toString();
}
